import { StyleSheet } from "react-native";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { Avatar } from "../Avatar";
import Box from "../Box";
import { LeaderboardMetricPill } from "./LeaderboardMetricPill";
import { LeaderboardYouBadge } from "./LeaderboardYouBadge";
import type { LeaderboardItem } from "./types";

export type LeaderboardRankRowProps = LeaderboardItem & {
	isCurrentUser?: boolean;
	showDivider?: boolean;
};

const ROW_HEIGHT = 60;
const AVATAR_SIZE = 38;

export const LeaderboardRankRow = ({
	rank,
	username,
	xp,
	isCurrentUser = false,
	showDivider = true,
}: LeaderboardRankRowProps) => {
	const theme = useAppTheme();

	return (
		<Box width="100%">
			<Box
				height={ROW_HEIGHT}
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				paddingLeft={12}
				paddingRight={16}
				backgroundColor={
					isCurrentUser ? theme.colors.elevation.level1 : "transparent"
				}
			>
				<Box
					flex={1}
					flexDirection="row"
					alignItems="center"
					gap={12}
					overflow="hidden"
				>
					<Text
						variant="bodySmall"
						style={[styles.rank, { color: theme.colors.primary }]}
					>
						{rank}
					</Text>
					<Box borderRadius={999} overflow="hidden">
						<Avatar size={AVATAR_SIZE} username={username} />
					</Box>
					<Box
						flexShrink={1}
						flexDirection="row"
						alignItems="center"
						gap={8}
						overflow="hidden"
					>
						<Text
							numberOfLines={1}
							variant="bodyMedium"
							style={[
								styles.username,
								isCurrentUser && styles.semibold,
								{ color: theme.colors.onSurface },
							]}
						>
							{username}
						</Text>
						{isCurrentUser && <LeaderboardYouBadge />}
					</Box>
				</Box>
				<LeaderboardMetricPill value={xp} highlighted={isCurrentUser} />
			</Box>
			{showDivider && !isCurrentUser ? (
				<Box height={1} backgroundColor={theme.colors.surfaceVariant} />
			) : null}
		</Box>
	);
};

const styles = StyleSheet.create({
	rank: {
		minWidth: 24,
		fontWeight: "600",
		textAlign: "right",
	},
	semibold: {
		fontWeight: "600",
	},
	username: {
		flexShrink: 1,
	},
});
